import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { FetcherWithComponents, useFetcher } from "react-router-dom";

import Button from "components/Button";
import FormWithValidation from "components/FormWithValidation";
import Spinner from "components/Spinner";
import SubmitButton from "components/SubmitButton";
import TaggedText from "components/TaggedText";
import Toaster, { ToasterId } from "components/Toaster";
import { IOnboardingQuestion } from "components/onboarding/OnboardingPage/Pages";
import QuestionsPage from "components/onboarding/OnboardingPage/QuestionsPage";

import useFormattedActionError from "hooks/useFormattedActionError";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";

import { RouteErrors } from "routes/data-routes/types";
import { useUser } from "routes/dataroutes/UserData";

import { ErrorMessage } from "./ErrorMessage";
import { ModalButtonsSection, ModalContent, ModalSections } from "./index";

import Messages from "misc/Messages";

import { fmtDate, fmtNumber } from "utils";
import WarningBox from "components/WarningBox";


export function CancelSubscriptionModalContent(
  {
    fetcher,
    closeModalCallback,
  }: {
    fetcher: FetcherWithComponents<RouteErrors.RouterActionError>,
    closeModalCallback: () => void,
  }
) {
  const user = useUser();

  return (
    <fetcher.Form method="POST" action="/billing-data/subscription/cancel">
      <ModalContent>
        <ModalSections>
          <div>Are you sure you want to cancel your subscription?</div>
          <div>
            Your subscription will remain with your current plan until the end
            of the billing period.{ " " }
            { user?.isBannedFromFree && (
              <>After that, your subscription will be cancelled and your API key will be removed.</>
            ) }
            { !user?.isBannedFromFree && (
              <>After that, you will be downgraded to the{ " " }
                <span className="font-bold">FREE</span> plan, with{ " " }
                <span className="font-bold">{ fmtNumber(1000) } API credits per month</span>.
              </>
            ) }
          </div>
          { !user?.isBannedFromFree && (
            <WarningBox
              variant="warning"
              title="Downgrading to the free plan will disable some Crawler features and pause scheduled jobs immediately."
            />
          ) }
          <ErrorMessage errorMessage={ useFormattedActionError(fetcher.data) } closeModalCallback={ closeModalCallback }/>
        </ModalSections>
        <ModalButtonsSection>
          <Button
            text="Keep Subscription"
            className="button button-secondary"
            onClick={ closeModalCallback }
            size="MD"
          />
          <SubmitButton
            text="Cancel Subscription"
            className="button button-primary destructive"
            size="MD"
            disabled={ fetcher.data?.error?.meta?.shouldNotRetry }
          />
        </ModalButtonsSection>
      </ModalContent>
    </fetcher.Form>
  );
}

export function CancellationSurveyModalContent(
  {
    closeModalCallback
  }: {
    closeModalCallback: () => void;
  }
) {
  const { activeSubscription } = useActiveSubscription();

  const cancellationSurveyFetcher = useFetcher();

  const secondaryQuestions: { [ index: string ]: string } = useMemo(() => { return {
    "Lower success rate than expected": "What was the success rate you experienced and/or for what domain?",
    "Price was too high": "Do you have any other feedback?",
    "Dissatisfied with support": "Do you have any other feedback what to improve on our side?",
    "Service was difficult to use": "What would have helped you?",
    "Experienced slow response times": "Can you tell us the domain(s) you experienced slow response time?",
    "Found alternative": "Can you tell us why you moved to another solution and which alternative you chose?",
    "Documentation was unclear": "What would have helped you?",
    "Specific feature was missing": "Can you tell us which feature you were looking for?",
    "Existing feature not working as expected": "Which feature were you not satisfied with and why?",
    "Experienced frequent downtimes": "What was the success rate you experienced and/or for what domain?",
    "Other/None of the above": "Do you have any other feedback?"
  } }, []);

  const firstQuestion: IOnboardingQuestion = useMemo(() => { return {
    id: "cancellation-reason",
    question: "Can you let us know why you decided to cancel?",
    type: "single-select",
    mandatory: true,
    answers: Object.keys(secondaryQuestions)
  } }, [ secondaryQuestions ]);

  const [ questions, setQuestions ] = useState([ firstQuestion ]);

  const onAnswerCallback = useCallback((event: ChangeEvent<HTMLFormElement>) => {
    if ((event.target.type === "radio") && event.target.value) {
      setQuestions([
        firstQuestion,
        {
          id: "cancellation-feedback",
          question: secondaryQuestions[event.target.value],
          type: "text-input",
          mandatory: false,
          answers: [ "" ]
        }
      ])
    }
  }, [ firstQuestion, secondaryQuestions ]);

  const [ answersSavedToastId, setAnswersSavedToastId ] = useState<ToasterId | undefined>();

  useEffect(() => {
    if (cancellationSurveyFetcher.data === "OK") {
      closeModalCallback();
      if (!answersSavedToastId) {
        setAnswersSavedToastId(
          Toaster.success("Subscription cancelled and answers sent!", "We are sorry to see you go. Thank you for participating in our survey.")
        );
      }
    }
  }, [ cancellationSurveyFetcher.data, closeModalCallback, answersSavedToastId, setAnswersSavedToastId ]);


  return (
    <ModalContent>
        <div className="flex flex-col text-gray dark:text-neutral-600">
        <div>
          <p>We're sorry to see you go!</p>
          <p>Your subscription was cancelled and will end on { fmtDate(new Date((activeSubscription?.currentTermEnd || 0) * 1000)) }.</p>
        </div>

        <FormWithValidation
          method="POST"
          action="/billing-data/subscription/cancellation-survey"
          onChange={ onAnswerCallback }
          fetcher={ cancellationSurveyFetcher }
        >
          <QuestionsPage page={{ questions: questions }} questionsClassName="font-semibold" showQuestionsHint={ false } />

          <div className="flex flex-row justify-end pt-8">
            <SubmitButton
              text="send"
              checkFormValidity
              disabled={ cancellationSurveyFetcher.state !== "idle" || (cancellationSurveyFetcher.data === "OK") }
            />
          </div>
        </FormWithValidation>

      </div>
    </ModalContent>
  );
}

export function CheckingUnpaidInvoicesModalContent() {
  return (
    <ModalContent>
      <ModalSections>
        <div className="flex flex-row gap-x-3">
          <p>Checking your invoices...</p>
          <Spinner className="w-5 h-5 animate-spin" />
        </div>
      </ModalSections>
    </ModalContent>
  );
}

export function DisabledBecauseOfUnpaidInvoicesModalContent(
  {
    closeModalCallback
  }: {
    closeModalCallback: () => void;
  }
) {
  return (
    <ModalContent>
      <ModalSections>
        <TaggedText message={ Messages.cannotCancelBecauseOfUnpaidInvoicesMessage } tagCallbacks={ { 'contact_support': closeModalCallback } } />
      </ModalSections>
      <ModalButtonsSection>
        <Button
          text="Ok"
          className="button button-primary"
          onClick={ closeModalCallback }
          size="MD"
        />
      </ModalButtonsSection>
    </ModalContent>
  );
}
